/**
 * London Smoke & Cure — Xero customer invoices feature gate.
 *
 * Build-time env (Hosting rebuild required):
 * - REACT_APP_XERO_INVOICES_URL — Cloud Run base URL
 * - REACT_APP_XERO_INVOICES_ENABLED — baseline enable for customer + admin tools
 * - REACT_APP_XERO_INVOICES_ADMIN_PREVIEW — Admin tools without customer tab
 *
 * Runtime overlay (Firestore config/xero_invoices_ui — no rebuild):
 * - killSwitch — stops all Xero SPA tools immediately
 * - exposeCustomerTools / exposeAdminTools — per-audience gates
 * Control panel on Admin Dashboard always remains reachable for Admins.
 */

function envFlag(name) {
  const raw = String(process.env[name] || '')
    .trim()
    .toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
}

/** @type {{ killSwitch: boolean, exposeCustomerTools: boolean, exposeAdminTools: boolean, payNowOffersEnabled: boolean, payNowRules: object[], payNowExcludedSkus: string[], loaded: boolean }} */
let runtimeOverlay = {
  killSwitch: false,
  exposeCustomerTools: true,
  exposeAdminTools: true,
  payNowOffersEnabled: false,
  payNowRules: [],
  payNowExcludedSkus: [],
  loaded: false,
};

const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

/**
 * Apply Firestore runtime flags (or defaults). Safe to call from any subscriber.
 */
export function applyXeroInvoicesRuntimeOverlay(flags = {}) {
  runtimeOverlay = {
    killSwitch: flags.killSwitch === true,
    exposeCustomerTools: flags.exposeCustomerTools !== false,
    exposeAdminTools: flags.exposeAdminTools !== false,
    payNowOffersEnabled: flags.payNowOffersEnabled === true,
    payNowRules: Array.isArray(flags.payNowRules) ? flags.payNowRules : [],
    payNowExcludedSkus: Array.isArray(flags.payNowExcludedSkus) ? flags.payNowExcludedSkus : [],
    loaded: true,
  };
  notify();
}

export function getXeroInvoicesRuntimeOverlay() {
  return { ...runtimeOverlay };
}

export function subscribeXeroInvoicesRuntimeOverlay(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test helper — reset overlay between Jest cases. */
export function __resetXeroInvoicesRuntimeOverlayForTests() {
  runtimeOverlay = {
    killSwitch: false,
    exposeCustomerTools: true,
    exposeAdminTools: true,
    payNowOffersEnabled: false,
    payNowRules: [],
    payNowExcludedSkus: [],
    loaded: false,
  };
  notify();
}

export function resolveXeroInvoicesBaseUrl() {
  return String(process.env.REACT_APP_XERO_INVOICES_URL || '')
    .trim()
    .replace(/\/+$/, '');
}

export function isXeroInvoicesApiConfigured() {
  return !!resolveXeroInvoicesBaseUrl();
}

/** Env baseline master flag (Hosting). */
export function isXeroInvoicesEnvFeatureEnabled() {
  return envFlag('REACT_APP_XERO_INVOICES_ENABLED');
}

/** Env admin-preview (Hosting). */
export function isXeroInvoicesEnvAdminPreviewEnabled() {
  return envFlag('REACT_APP_XERO_INVOICES_ADMIN_PREVIEW');
}

/**
 * Effective feature flag after runtime kill switch.
 * Used as the broad “Xero tools may run” signal.
 */
export function isXeroInvoicesFeatureEnabled() {
  if (runtimeOverlay.killSwitch) return false;
  return isXeroInvoicesEnvFeatureEnabled();
}

export function isXeroInvoicesAdminPreviewEnabled() {
  if (runtimeOverlay.killSwitch) return false;
  return isXeroInvoicesEnvAdminPreviewEnabled();
}

export function isXeroInvoicesKillSwitchOn() {
  return runtimeOverlay.killSwitch === true;
}

/**
 * Customer Account → Invoices tab / pay surfaces.
 */
export function isXeroInvoicesCustomerSurfaceEnabled() {
  if (runtimeOverlay.killSwitch) return false;
  if (!runtimeOverlay.exposeCustomerTools) return false;
  return isXeroInvoicesEnvFeatureEnabled() && isXeroInvoicesApiConfigured();
}

/**
 * Soft Pay now after checkout may run (master switch + customer surface + not killed).
 */
export function isXeroPayNowOffersEnabled() {
  if (runtimeOverlay.killSwitch) return false;
  if (!runtimeOverlay.payNowOffersEnabled) return false;
  return isXeroInvoicesCustomerSurfaceEnabled();
}

export function getXeroPayNowRules() {
  return Array.isArray(runtimeOverlay.payNowRules) ? [...runtimeOverlay.payNowRules] : [];
}

/** Global weigh-on-ship (etc.) SKU exclusions for Submit and pay. */
export function getXeroPayNowExcludedSkus() {
  return Array.isArray(runtimeOverlay.payNowExcludedSkus)
    ? [...runtimeOverlay.payNowExcludedSkus]
    : [];
}

/**
 * Invoicer desk (display name **Xero day**), Admin order invoice tools, Align, Raise, etc.
 * Does not gate the Admin Dashboard Xero control panel itself.
 */
export function isXeroInvoicesAdminSurfaceEnabled() {
  if (runtimeOverlay.killSwitch) return false;
  if (!runtimeOverlay.exposeAdminTools) return false;
  return (
    isXeroInvoicesEnvFeatureEnabled() || isXeroInvoicesEnvAdminPreviewEnabled()
  );
}

/**
 * Admin Dashboard control panel — always allowed when URL is set or env preview/enabled,
 * even under kill switch, so operators can reconnect / re-enable.
 */
export function isXeroInvoicesControlPanelEnabled() {
  return (
    isXeroInvoicesApiConfigured() ||
    isXeroInvoicesEnvFeatureEnabled() ||
    isXeroInvoicesEnvAdminPreviewEnabled()
  );
}

export function describeXeroInvoicesConfig() {
  const baseUrl = resolveXeroInvoicesBaseUrl();
  return {
    baseUrl: baseUrl || null,
    apiConfigured: !!baseUrl,
    featureEnabled: isXeroInvoicesFeatureEnabled(),
    envFeatureEnabled: isXeroInvoicesEnvFeatureEnabled(),
    adminPreview: isXeroInvoicesAdminPreviewEnabled(),
    envAdminPreview: isXeroInvoicesEnvAdminPreviewEnabled(),
    customerSurface: isXeroInvoicesCustomerSurfaceEnabled(),
    adminSurface: isXeroInvoicesAdminSurfaceEnabled(),
    controlPanel: isXeroInvoicesControlPanelEnabled(),
    killSwitch: isXeroInvoicesKillSwitchOn(),
    exposeCustomerTools: runtimeOverlay.exposeCustomerTools,
    exposeAdminTools: runtimeOverlay.exposeAdminTools,
    payNowOffersEnabled: runtimeOverlay.payNowOffersEnabled,
    payNowRules: getXeroPayNowRules(),
    payNowExcludedSkus: getXeroPayNowExcludedSkus(),
    payNowOffersLive: isXeroPayNowOffersEnabled(),
    runtimeLoaded: runtimeOverlay.loaded,
    detached:
      !isXeroInvoicesEnvFeatureEnabled() &&
      !isXeroInvoicesEnvAdminPreviewEnabled(),
  };
}
